/**
 * CharterMegaMenu — hover/tap-to-open mega-menu listing all 50 fishing
 * charter operators grouped by region.  Lives inside the Tackle.au
 * topbar so buyers shopping for gear can spot the right charter
 * without leaving the site.
 *
 * Tracks every operator click via /api/fish-map/track-charter-view so
 * the operator gets a (rate-limited) Resend email when anglers eye
 * their listing.
 */
import { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronDown, MapPin, Ship, Star } from "lucide-react";
import "./CharterMegaMenu.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

export default function CharterMegaMenu() {
  const [open, setOpen] = useState(false);
  const [operators, setOperators] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const closeTimerRef = useRef(null);

  // Retry-on-failure (R2 backfill saturates backend periodically)
  useEffect(() => {
    let cancelled = false;
    const fetchOperators = async (attempt = 1) => {
      setLoadState("loading");
      try {
        const r = await api.get(`/api/fishing-tours`,
          { params: { limit: 80 }, timeout: 25000 });
        if (cancelled) return;
        const list = r.data?.results || [];
        if (list.length > 0) {
          setOperators(list); setLoadState("ok");
        } else if (attempt < 3) {
          setTimeout(() => fetchOperators(attempt + 1), 1800 * attempt);
        } else {
          setLoadState("error");
        }
      } catch (_) {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => fetchOperators(attempt + 1), 1800 * attempt);
        } else {
          setLoadState("error");
        }
      }
    };
    fetchOperators();
    return () => { cancelled = true; };
  }, []);

  const retryFetch = () => {
    // simple manual retry — re-runs the effect by mounting fresh state
    setLoadState("loading");
    api.get(`/api/fishing-tours`, { params: { limit: 80 }, timeout: 25000 })
       .then(r => {
         setOperators(r.data?.results || []);
         setLoadState(r.data?.results?.length ? "ok" : "error");
       })
       .catch(() => setLoadState("error"));
  };

  const grouped = useMemo(() => {
    const buckets = {
      "🇦🇺 SEQ":       [],
      "🇦🇺 Cairns / GBR":  [],
      "🇦🇺 NT · Top End":  [],
      "🇦🇺 Other AU":      [],
      "🇳🇿 New Zealand":  [],
      "🌍 International":  [],
    };
    const SEQ_REGIONS = new Set([
      "moreton-bay", "redcliffe", "cleveland", "bribie-island",
      "brisbane", "gold-coast", "gold-coast-broadwater", "tweed-heads",
      "sunshine-coast", "noosa", "caloundra", "hervey-bay",
    ]);
    operators.forEach(op => {
      const cc = op.country_code;
      const rs = op.region_slug;
      if (cc === "AU" && SEQ_REGIONS.has(rs))       buckets["🇦🇺 SEQ"].push(op);
      else if (cc === "AU" && rs === "cairns")      buckets["🇦🇺 Cairns / GBR"].push(op);
      else if (cc === "AU" && rs === "whitsundays") buckets["🇦🇺 Cairns / GBR"].push(op);
      else if (cc === "AU" && rs === "darwin")      buckets["🇦🇺 NT · Top End"].push(op);
      else if (cc === "AU")                          buckets["🇦🇺 Other AU"].push(op);
      else if (cc === "NZ")                          buckets["🇳🇿 New Zealand"].push(op);
      else                                           buckets["🌍 International"].push(op);
    });
    return buckets;
  }, [operators]);

  const total = operators.length;

  const handleEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 180);
  };

  const trackClick = (op) => {
    try {
      api.post(`/api/fish-map/track-charter-view`, {
        charter_slug: op.slug, source: "tackle-mega-menu",
        region: op.region,
      });
    } catch (_) { /* ignore */ }
  };

  return (
    <div
      className="cmm"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid="charter-mega-menu"
    >
      <button
        className={`cmm__trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="charter-mega-menu-trigger"
      >
        Charters <ChevronDown size={14}/>
      </button>

      {open && (
        <div className="cmm__panel" role="menu" data-testid="charter-mega-menu-panel">
          <div className="cmm__head">
            <div>
              <h3>Fishing Charter Directory</h3>
              <p>
                {loadState === "loading" ? "Loading operators…" :
                 loadState === "error"   ? "Tap retry to load operators" :
                 `${total} vetted operators · enquire direct · no booking fees`}
              </p>
            </div>
            <div className="cmm__head-cta">
              <Link to="/fish-map" className="cmm__head-btn cmm__head-btn--map"
                    data-testid="charter-mega-menu-fish-map">
                <MapPin size={13}/> Fish Map
              </Link>
              <Link to="/fishing-tours" className="cmm__head-btn"
                    data-testid="charter-mega-menu-all">
                <Ship size={13}/> Browse all →
              </Link>
            </div>
          </div>

          {loadState === "loading" && (
            <div className="cmm__loading" data-testid="charter-mega-menu-loading">
              <span className="cmm__spinner"/> Loading 50 operators…
            </div>
          )}

          {loadState === "error" && (
            <div className="cmm__error" data-testid="charter-mega-menu-error">
              <p>Couldn&apos;t reach the directory. Backend may be busy.</p>
              <button
                onClick={retryFetch}
                className="cmm__retry"
                data-testid="charter-mega-menu-retry"
              >
                Retry
              </button>
            </div>
          )}

          {loadState === "ok" && (
            <div className="cmm__grid">
              {Object.entries(grouped).map(([label, list]) => (
                list.length > 0 && (
                  <section key={label} className="cmm__col">
                    <h4>{label} <span>{list.length}</span></h4>
                    <ul>
                      {list.slice(0, 8).map(op => (
                        <li key={op.slug}>
                          <Link
                            to={`/fishing-tours/${op.slug}`}
                            onClick={() => trackClick(op)}
                            data-testid={`cmm-charter-${op.slug}`}
                          >
                            {op.featured && <Star size={10} className="cmm__star"/>}
                            <span className="cmm__name">{op.name}</span>
                            <span className="cmm__region">{op.region}</span>
                          </Link>
                        </li>
                      ))}
                      {list.length > 8 && (
                        <li>
                          <Link
                            to={`/fishing-tours?region=${list[0].region_slug}`}
                            className="cmm__more"
                          >
                            + {list.length - 8} more in {label.replace(/^[^\s]+\s/, "")} →
                          </Link>
                        </li>
                      )}
                    </ul>
                  </section>
                )
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
